import { all, fork, put, takeEvery, takeLatest, call, delay } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/registration/GuestInhouseActionType';
import * as actionCreators from 'appRedux/actions/registration/GuestInhouse';
import { detailType, verifyType } from 'appRedux/reducers/registration/GuestInhouseType';
import { detailInitialState } from 'appRedux/reducers/registration/GuestInhouse';
import * as actionCreatorsExpectedArrival from 'appRedux/actions/expected/ExpectedDeparture';

import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { APIGetRequest, APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { ApiResponse } from 'constants/ActionTypes';
import { AppState } from 'appRedux/reducers/ReducerState';
import { select } from 'redux-saga/effects';
import { Modal } from 'antd';
import { useRegisterStore } from '@/zusttandStore/register.store';
import { useAppStateStore } from '@/zusttandStore/appState.store';

const syncPrimaryGuestId = (guests: any[] | undefined, guestId?: number | string) => {
  if (!Array.isArray(guests) || guests.length === 0) return guests;
  if (guestId === undefined || guestId === null || guestId === '') return guests;
  return guests.map((guest, idx) => (idx === 0 ? { ...guest, id: guestId } : guest));
};

const unwrapRpcResponseData = (responseData: any) => responseData?.data ?? responseData ?? {};

function* guestInhouseRequestProcess(value: actionTypes.GuestInhouseRequest) {
  const { payload, sorter = undefined } = value;

  let keywords = payload;
  const guestInhouseInfo = {} as any;
  if (keywords.searchText) guestInhouseInfo.search_text = keywords.searchText;
  if (keywords.arrival) guestInhouseInfo.arrival = dayjs(keywords.arrival).format('YYYY-MM-DD');
  if (keywords.departure)
    guestInhouseInfo.departure = dayjs(keywords.departure).format('YYYY-MM-DD');
  if (keywords.stayDate)
    guestInhouseInfo.stay_date = dayjs(keywords.stayDate).format('YYYY-MM-DD');
  if (keywords.roomNumber) guestInhouseInfo.room_number = keywords.roomNumber;
  if (keywords.roomTypeId) guestInhouseInfo.roomtype_id = keywords.roomTypeId;
  if (keywords.channelName) guestInhouseInfo.channel_name = keywords.channelName;
  if (keywords.vipId) guestInhouseInfo.vip_id = keywords.vipId;
  if (keywords.memberCode) guestInhouseInfo.member_code = keywords.memberCode;
  if (keywords.company) guestInhouseInfo.company = keywords.company;
  if (keywords.nationId) guestInhouseInfo.nation_id = keywords.nationId;
  if (keywords.ratePlanId) guestInhouseInfo.rateplan_id = keywords.ratePlanId;
  if (keywords.carplateNo) guestInhouseInfo.carplate_no = keywords.carplateNo;
  if (keywords.registerId) guestInhouseInfo.register_id = keywords.registerId;
  if (keywords.bookingId) guestInhouseInfo.booking_id = keywords.bookingId;

  const roomStatus = [];
  if (keywords.statusINH) roomStatus.push('I');
  if (keywords.statusCO) roomStatus.push('O');

  guestInhouseInfo.statuses = isEmpty(roomStatus) ? 'I,O' : roomStatus.toString();

  guestInhouseInfo.include_archive = !!keywords.includeArchive;

  /* Set paging with sort_field/sort_order inside paging */
  const pagingObj: any = { page: keywords.current, limit: keywords.limit };
  if (
    sorter !== undefined &&
    sorter.fieldArray &&
    sorter.fieldArray.length > 0 &&
    sorter.orderArray &&
    sorter.orderArray.length > 0
  ) {
    pagingObj.sort_field = sorter.fieldArray[0];
    pagingObj.sort_order = sorter.orderArray[0];
  }
  guestInhouseInfo.paging = pagingObj;

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/rpc/registration/inhouse/search`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const response: ApiResponse = yield APIPostRequest(url, guestInhouseInfo, API_PMS_HEADERS);

    if (response.data?.result_code === '0') {
      /* if no error put everything to reducer */
      const { items, paging } = response.data.result_data;
      yield put(actionCreators.guestInhouseDataSourceSuccess({ results: items, paging }));
      yield delay(100);
      yield put(
        actionCreators.guestInhouseSetFilter({
          ...payload,
          current: paging?.current,
          limit: paging?.limit,
          pageSize: paging?.limit,
          total: paging?.total_rows,
        })
      );
      yield delay(100);
    } else {
      /* Show error message and signout */
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'GuestInhousePreview', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'GuestInhousePreview', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield put(actionCreators.guestInhouseProcessReset());
  }
}
function* guestInhouseCheckOutProcess({ payload }: actionTypes.GuestInhouseCheckOut) {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/checkOut/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    let API_DATABODY: any = {
      register_id: payload.register_id,
    };
    if (payload.early_checkout_reason?.trim()) {
      API_DATABODY.early_checkout_reason = payload.early_checkout_reason;
    }
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);
    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = { status: true, action: 'checkOut', message: result_msg, result_code };
      yield put(actionCreatorsExpectedArrival.expectedDepartureProcessSuccess(processStatus));
      yield put(actionCreatorsExpectedArrival.expectedDepartureProcessReset());

      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message } = response.data;
      const processStatus = { status: false, action: 'checkOut', message };
      yield put(actionCreatorsExpectedArrival.expectedDepartureProcessSuccess(processStatus));
      yield put(actionCreatorsExpectedArrival.expectedDepartureProcessReset());

      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        const { data } = response.data;

        /* if data is not null force signout */
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'checkOut', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseCancelCheckInProcess({ payload }: actionTypes.GuestInhouseCancelCheckIn) {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/cancelCheckIn/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = { register_id: payload };
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'cancelCheckIn',
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message } = response.data;
      const processStatus = { status: false, action: 'cancelCheckIn', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        const { data } = response.data;

        /* if data is not null force signout */
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'cancelCheckIn', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseCancelCheckOutProcess({ payload }: actionTypes.GuestInhouseCancelCheckOut) {
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/cancelCheckOut/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = payload;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'cancelCheckOut',
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message } = response.data;
      const processStatus = { status: false, action: 'cancelCheckOut', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        const { data } = response.data;

        /* if data is not null force signout */
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'cancelCheckOut', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseDetailRequestProcess({ payload }: actionTypes.GuestInhouseDetailRequest) {
  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/rpc/registration/inhouse/get`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
  const registerId = typeof payload === 'object' ? (payload as any).register_id : payload;
  const source = typeof payload === 'object' ? ((payload as any).source || 'any') : 'any';

  try {
    const response: ApiResponse = yield APIPostRequest(url, { register_id: registerId, source }, API_PMS_HEADERS);
    if (response.data?.result_code === '0') {
      const guestInhouseDetail: detailType = response.data.result_data;

      yield put(actionCreators.guestInhouseDetailDataSourceSuccess(guestInhouseDetail));
      yield put(actionCreators.guestInhouseBookingSummarySuccess(guestInhouseDetail.summary));
      /* Keep original roomtype from checkin detail */
      const { room_id, room_number, roomtype_code, roomtype_id, roomtype_name } =
        guestInhouseDetail;
      yield put(
        actionCreators.guestInhouseCheckinRoomOriginal({
          room_id,
          room_number,
          roomtype_code,
          roomtype_id,
          roomtype_name,
        })
      );
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'guestinhouse_detail', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        //if data is not null force signout
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'guestinhouse_detail', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseVerifyRateProcess({ payload }: actionTypes.GuestInhouseVerifyRate) {
  // Extracting payload data
  const { actionValidate, keep_original_rate, ...others } = payload;
  const {
    origin_id,
    market_id,
    arrival,
    departure,
    register_id,
    arrival_time,
    departure_time,
    rateplan_id,
    room_id,
    room_number,
    adult,
    child,
    infant,
    check_rate,
    check_avail,
    upgrade_type,
    upgrade_reason,
    channel_id,
  } = payload;
  const isWalkInNewPage = window.location.pathname === '/main/guestinhouse/walkIn/new';
  // Selecting previous guest in-house data for comparison
  const previousData: verifyType = yield select(
    (state: AppState) => state.guestInHouse.detailDataSource as unknown as verifyType
  );
  // Constructing new verify data
  const hasSelectedRatePlan = localStorage.getItem('hasSelectedRatePlan') === 'true';
  const cleanRateplanId = isWalkInNewPage
    ? !hasSelectedRatePlan
      ? undefined
      : rateplan_id
    : rateplan_id;
  const verifyInfo: verifyType = {
    register_id: register_id || null,
    rateplan_id: cleanRateplanId,
    arrival_date: arrival,
    departure_date: departure,
    room_id: room_id || undefined,
    room_number: room_id ? undefined : room_number,
    adult,
    child,
    infant,
    open_rate: others?.open_rate || payload?.open_rate,
    checkin_data: {
      ...others,
      adult,
      child,
      infant,
      keep_original_rate,
    },
    check_rate,
    check_avail,
    upgrade_type,
    upgrade_reason,
    keep_original_rate,
    origin_id,
    market_id,
    channel_id: channel_id || 0,
  };
  // Detect changed fields
  const changedData: Record<string, { old: any; new: any }> = {};
  if (previousData) {
    Object.entries(verifyInfo).forEach(([key, newValue]) => {
      const mappedKey =
        key === 'arrival_date' ? 'arrival' : key === 'departure_date' ? 'departure' : key;
      const oldValue = previousData[mappedKey as keyof verifyType];

      if (oldValue !== newValue) {
        changedData[key] = { old: oldValue, new: newValue };
      }
    });
  }
  try {
    // Fetch access token
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;

    // API request
    const url = `${API_URL_V1}pms/inventory/verifyRateAndAvail/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = {
      register_id: verifyInfo.register_id,
      rateplan_id: verifyInfo.rateplan_id,
      arrival_date: verifyInfo.arrival_date,
      departure_date: verifyInfo.departure_date,
      room_id: verifyInfo.room_id,
      adult: verifyInfo.adult,
      child: verifyInfo.child,
      infant: verifyInfo.infant,
      check_rate: verifyInfo.check_rate,
      check_avail: verifyInfo.check_avail,
      upgrade_type: verifyInfo.upgrade_type,
      upgrade_reason: verifyInfo.upgrade_reason,
      room_number: verifyInfo.room_number,
      keep_original_rate: verifyInfo.keep_original_rate,
      channel_id: verifyInfo.channel_id || 0,
      market_id: verifyInfo.market_id || 0,
      origin_id: verifyInfo.origin_id || 0,
      checkin_data: {
        data_old: {
          adult: previousData?.adult || '',
          arrival_date: previousData?.arrival_date || '',
          child: previousData?.child || '',
          departure_date: previousData?.departure || '',
          infant: previousData?.infant || '',
          rateplan_id: previousData?.rateplan_id || '',
          register_id: previousData?.register_id || '',
          room_id: previousData?.room_id || '',
        },
        ...previousData,
        adult,
        child,
        infant,
        keep_original_rate: verifyInfo.keep_original_rate,
        open_rate: verifyInfo.open_rate,
        rate_schedule:
          verifyInfo.checkin_data?.rate_schedule &&
            JSON.stringify(verifyInfo.checkin_data.rate_schedule) !==
            JSON.stringify(previousData?.checkin_data?.rate_schedule)
            ? verifyInfo.checkin_data.rate_schedule
            : previousData?.checkin_data?.rate_schedule,
      },
    };
    const currentPath = window.location.pathname.split('/').slice(2, 4).join('/');

    const API_DATABODY_CURRENT = {
      ...verifyInfo,
      channel_id: verifyInfo.channel_id || 0,
      open_rate: verifyInfo.open_rate,
    };
    const requestData =
      currentPath === 'guestinhouse/checkin' ? API_DATABODY_CURRENT : API_DATABODY;

    const response: ApiResponse = yield APIPostRequest(url, requestData, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg, result_data } = response.data;

      console.log('response.data', response.data);

      if (result_code !== '0' && actionValidate !== 'verifyRateByRoomNumber') {
        // verifyRateByRoomNumber errors are handled by searchRoomNumber component directly
        useAppStateStore.getState().setErrorMessage(result_msg || 'Cannot verify rate and availability');
      } else {
        useAppStateStore.getState().setErrorMessage('');
      }

      // Soft-fail condition for result_code "4" and null result_data (e.g., room occupied)
      if (result_code === '4' && !result_data) {
        const fullPath = window.location.pathname;
        if (fullPath === '/main/guestinhouse/walkIn/new') {
          localStorage.setItem('isWalkInVerified', 'false');
        }
        const processStatus = {
          status: false,
          action: actionValidate,
          message: result_msg,
          result_code,
        };
        if (fullPath === '/main/guestinhouse/walkIn/new') {
          console.warn('⚠️ walkIn/new fallback: setting verifyInfo as detailDataSource');
          yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
          yield put(actionCreators.guestInhouseDetailDataSourceSuccess(previousData));
          yield put(
            actionCreators.guestInhouseBookingSummarySuccess(verifyInfo.checkin_data.summary)
          );
        } else {
          yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
          yield put(actionCreators.guestInhouseDetailDataSourceSuccess(previousData));
          yield put(actionCreators.guestInhouseBookingSummarySuccess(previousData.summary));
        }
        // Delay reset so components can react to the process status before it clears
        yield delay(300);
        yield put(actionCreators.guestInhouseProcessReset());
        return;
      } else {
        localStorage.setItem('isWalkInVerified', 'true');
      }

      const processStatus = {
        status: result_code === '0',
        action: actionValidate,
        message: result_msg,
        result_code,
      };

      // Dispatch success status BEFORE reset so components can react to the action
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));

      if (result_code !== '0') {
        yield put(actionCreators.guestInhouseDetailDataSourceSuccess(previousData));
        yield put(actionCreators.guestInhouseBookingSummarySuccess(previousData.summary));
        // Delay reset so components can react to the process status before it clears
        yield delay(300);
        yield put(actionCreators.guestInhouseProcessReset());
        return;
      }

      if (result_data) {
        const updatedData = {
          ...others,
          ...result_data,
          extra_child: others.extra_child ?? result_data.extra_child ?? 0,
          extra_adult: others.extra_adult ?? result_data.extra_adult ?? 0,
          infant: others.infant ?? result_data.infant ?? 0,
          arrival_time: dayjs(arrival_time, 'HH:mm').format('HH:mm'),
          departure_time: dayjs(departure_time, 'HH:mm').format('HH:mm'),
          origin_id: result_data.origin_id ?? others.origin_id ?? previousData.origin_id ?? null,
          checkin_data: {
            ...result_data.checkin_data,
            open_rate: verifyInfo.open_rate,
          },
        };

        // Updating Redux store with new data
        yield put(actionCreators.guestInhouseDetailDataSourceSuccess(updatedData));
        // Update booking summary
        yield put(actionCreators.guestInhouseBookingSummarySuccess(result_data.summary));
      }

      // Delay reset so components can react to process status before it clears
      yield delay(300);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      // Handle API errors
      const { message } = response.data;
      yield put(
        actionCreators.guestInhouseProcessSuccess({
          status: false,
          action: actionValidate,
          message,
        })
      );
      yield put(actionCreators.guestInhouseProcessReset());

      if (response.status === 403 && response.data?.data) {
        yield put(userSignOut());
      }
    }
  } catch (error) {
    console.error('❌ Error in verifyRate API call:', error);
    yield put(
      actionCreators.guestInhouseProcessSuccess({
        status: false,
        action: 'verifyRate',
        message: error,
      })
    );
    yield put(actionCreators.guestInhouseProcessReset());
  }
}

function* guestInhouseNewProcess({ payload }: actionTypes.GuestInhouseNew) {
  const guestInhouseInfo = {
    guest_id: payload?.guest_id,
    room_id: payload?.room_id,
    checkin_data: {
      ...payload,
      arrival_time: dayjs(payload.arrival_time, 'HH:mm', true).isValid()
        ? dayjs(payload.arrival_time, 'HH:mm').format('HH:mm')
        : '00:00',
      departure_time: `${payload.departure_time}:00`,
      // departure_time: dayjs(payload.departure_time, 'HH:mm:ss', true).isValid()
      //   ? dayjs(payload.departure_time, 'HH:mm:ss').format('HH:mm:ss')
      //   : '00:00:00',
    },
  };

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/walkIn/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = guestInhouseInfo;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'guestInhouseWalkIn',
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'guestInhouseWalkIn', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'guestInhouseWalkIn', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}

function* guestInhouseQuickWalkInProcess({ payload }: actionTypes.GuestInhouseQuickWalkIn) {
  try {
    // Optional: open_rate (for plan_type === 9)
    const normalizedOpenRate = payload?.open_rate
      ? {
        amount: Number(payload?.open_rate?.amount ?? 0),
        // allow explicit 0, otherwise null when missing
        mealtype_id:
          payload?.open_rate?.mealtype_id === 0 ? 0 : payload?.open_rate?.mealtype_id ?? null,
        extra_adult_rate: Number(payload?.open_rate?.extra_adult_rate ?? 0),
        extra_child_rate: Number(payload?.open_rate?.extra_child_rate ?? 0),
      }
      : undefined;

    const body = {
      room_id: Number(payload?.room_id),
      arrival: payload?.arrival ? dayjs(payload.arrival).format('YYYY-MM-DD') : null,
      departure: payload?.departure ? dayjs(payload.departure).format('YYYY-MM-DD') : null,
      adult: Number(payload?.adult ?? 1),
      child: Number(payload?.child ?? 0),
      infant: Number(payload?.infant ?? 0),
      reservation_type_id: payload?.reservation_type_id ?? null,
      channel_id: payload?.channel_id ?? null,
      voucher_no: payload?.voucher_no ?? null,
      origin_id: payload?.origin_id ?? null,
      extra_adult: Number(payload?.extra_adult ?? 0),
      extra_child: Number(payload?.extra_child ?? 0),
      extra_infant: Number(payload?.extra_infant ?? 0),
      no_amend: Boolean(payload?.no_amend ?? false),
      no_cancel: Boolean(payload?.no_cancel ?? false),
      no_refund: Boolean(payload?.no_refund ?? false),
      rate_id: payload?.rate_id ?? null,
      rateplan_id: payload?.rateplan_id ?? null,
      rate_schedule: Array.isArray(payload?.rate_schedule)
        ? payload.rate_schedule.map((r: any) => ({
          date: r?.date ? dayjs(r.date).format('YYYY-MM-DD') : null,
          amount: Number(r?.amount ?? 0),
          extra_adult_rate: Number(r?.extra_adult_rate ?? 0),
          extra_child_rate: Number(r?.extra_child_rate ?? 0),
          extra_infant_rate: Number(r?.extra_infant_rate ?? 0),
          comp: Boolean(r?.comp ?? false),
          mealtype_id: r?.mealtype_id ?? null,
        }))
        : [],
      ...(normalizedOpenRate ? { open_rate: normalizedOpenRate } : {}),
      guest: payload?.guest ?? null,
      note: typeof payload?.note === 'string' ? payload.note.trim() : payload?.note ?? null,
    };

    // Access token & headers
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/rpc/registration/quick_walkin`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

    // Call API
    const response: ApiResponse = yield APIPostRequest(url, body, API_PMS_HEADERS);

    if (response.status === 200) {
      const apiData = unwrapRpcResponseData(response.data);
      const { result_code, result_msg, msg_code, result_data } = apiData;

      // ----------- For Issue card on room plan page ------
      const registerId = result_data?.register_id;

      if (registerId) {
        useRegisterStore.getState().setRegisterId(registerId);
        console.log('[SAGA] store after set =', useRegisterStore.getState());
      }

      // --------------------------------------------------

      const processStatus = {
        status: true,
        action: 'quickWalkIn',
        message: result_msg ?? 'Quick walk-in success.',
        result_code,
        result_msg,
        msg_code,
        result_data,
        api_status: response.status,
      };

      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      // Do not reset here. RoomPlan can be busy rendering a large calendar and may
      // miss a short-lived process state. The quick-walk-in modal resets it after
      // showing the user-facing success/error dialog.
    } else {
      const { message, data } = response.data || { message: 'Unknown error' };
      const processStatus = { status: false, action: 'quickWalkIn', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      if (response.status === 403 && data) {
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'quickWalkIn', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
  }
}
function* guestInhouseUpdateProcess({ payload }: actionTypes.GuestInhouseUpdate) {
  console.log('payload', payload.departure_time);
  const normalizedGuests = syncPrimaryGuestId(payload?.guests, payload?.guest_id);
  const formattedArrivalTime = dayjs.isDayjs(payload.arrival_time)
    ? payload.arrival_time.format('HH:mm')
    : typeof payload.arrival_time === 'string' && payload.arrival_time.length >= 5
      ? payload.arrival_time.substring(0, 5)
      : '00:00';
  const formattedDepartureTime = dayjs.isDayjs(payload?.departure_time)
    ? payload.departure_time.format('HH:mm')
    : payload.departure_time || '00:00';
  const guestInhouseInfo = {
    register_id: payload?.register_id,
    checkin_data: {
      ...payload,
      guests: normalizedGuests ?? payload?.guests,
      arrival_time: formattedArrivalTime,
      departure_time: formattedDepartureTime,
    },
  };

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/guestInHouse/update/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = guestInhouseInfo;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'updateGuestInhouse',
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      // ✅ เพิ่ม delay เพื่อให้ modal มีเวลาแสดง (1000ms = 1 วินาที)
      yield delay(1000);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'updateGuestInhouse', message };
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      // ✅ เพิ่ม delay เพื่อให้ modal มีเวลาแสดง (1000ms = 1 วินาที)
      yield delay(1000);
      yield put(actionCreators.guestInhouseProcessReset());
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'updateGuestInhouse', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    // ✅ เพิ่ม delay เพื่อให้ modal มีเวลาแสดง (1000ms = 1 วินาที)
    yield delay(1000);
    yield put(actionCreators.guestInhouseProcessReset());
  }
}
function* guestInhouseBookingSummaryProcess({
  payload,
}: actionTypes.GuestInhouseBookingSummaryRequest) {
  const arrival_time = dayjs(payload.arrival_time).format('HH:mm');
  const departure_time = dayjs(payload.departure_time).format('HH:mm');

  const reformData = { ...payload, arrival_time, departure_time };

  const summaryInfo = { checkin_data: { ...reformData } };
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/checkInSummary/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = summaryInfo;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      yield put(actionCreators.guestInhouseBookingSummarySuccess(response.data));
    } else {
    }
  } catch (error) { }
}
function* guestInhouseCheckinVerifyProcess({ payload }: actionTypes.GuestInhouseCheckinVerify) {
  const { booking_item, room_id, guest_id } = payload;
  let checkinDetail = {} as any;

  // Only send room_id if it's explicitly provided (not null/undefined)
  // When room is unblocked, room_id should not be sent
  if (room_id && room_id !== 'null' && room_id !== 'undefined') {
    checkinDetail.room_id = room_id;
  }
  if (guest_id) checkinDetail.guest_id = guest_id;

  let queryString = Object.keys(checkinDetail)
    .map((key) => `${key}=${checkinDetail[key]}`)
    .join('&');

  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/reservation/booking/checkInDetail/${booking_item}/?${queryString}`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

  try {
    const response: ApiResponse = yield APIGetRequest(url, API_PMS_HEADERS);
    if (response.status === 200) {
      // Format arrival_time and departure_time before dispatching
      const rawArrival = response.data.arrival_time;
      const rawDeparture = response.data.departure_time;
      const guestInhouseDetail: detailType = {
        ...response.data,
        arrival_time: typeof rawArrival === 'string' && rawArrival.length >= 5
          ? rawArrival.substring(0, 5)
          : '00:00',
        departure_time: typeof rawDeparture === 'string' && rawDeparture.length >= 5
          ? rawDeparture.substring(0, 5)
          : '00:00',
      };
      yield put(actionCreators.guestInhouseDetailDataSourceSuccess(guestInhouseDetail));
      yield put(actionCreators.guestInhouseBookingSummarySuccess(guestInhouseDetail.summary));
      /* Keep original roomtype from checkin detail - only if room data exists */
      const {
        room_id: responseRoomId,
        room_number,
        roomtype_code,
        roomtype_id,
        roomtype_name,
      } = guestInhouseDetail;
      yield put(
        actionCreators.guestInhouseCheckinRoomOriginal({
          room_id: responseRoomId,
          room_number,
          roomtype_code,
          roomtype_id,
          roomtype_name,
        })
      );
    } else {
      const { message, data } = response;
      const processStatus = { status: false, action: 'guestInhouseCheckinVerify', message };
      // Dispatch empty data to stop loader
      yield put(actionCreators.guestInhouseDetailDataSourceSuccess(detailInitialState));
      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        //if data is not null force signout
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    console.error('❌ Error on get guest inhouse checkin verify : ', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to load check-in details';
    const processStatus = {
      status: false,
      action: 'guestInhouseCheckinVerify',
      message: errorMessage,
    };
    // Dispatch empty data to stop loader
    yield put(actionCreators.guestInhouseDetailDataSourceSuccess(detailInitialState));
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseCheckinProcess({ payload }: actionTypes.GuestInhouseCheckin) {
  const guestNewDataArray = JSON.parse(localStorage.getItem('guestNewDataArray') || '[]');
  const mainGuestId = guestNewDataArray.length > 0 ? guestNewDataArray[0].id : null;
  const guestInhouseInfo = {
    booking_item: payload?.booking_item,
    guest_id: mainGuestId || payload?.guest_id,
    room_id: payload?.room_id,
    upgrade_type: payload?.upgrade_type || null,
    upgrade_reason: payload?.upgrade_reason || null,
    checkin_data: {
      ...payload,
      guest_id: mainGuestId || payload?.guest_id,
      guests: Array.isArray(payload.guests)
        ? payload.guests.filter((g: { id: null }) => g.id !== null)
        : [],
      arrival_time: dayjs(payload.arrival_time, 'HH:mm', true).isValid()
        ? dayjs(payload.arrival_time, 'HH:mm').format('HH:mm')
        : '00:00',
      departure_time: dayjs(payload.departure_time, 'HH:mm:ss', true).isValid()
        ? dayjs(payload.departure_time, 'HH:mm:ss').format('HH:mm:ss')
        : '00:00:00',
    },
  };
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/registration/checkIn/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = guestInhouseInfo;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'guestInhouseCheckIn',
        message: result_msg,
        result_code,
      };

      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.guestInhouseProcessReset());
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'guestInhouseCheckIn', message };

      yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.guestInhouseProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'guestInhouseCheckIn', message: error };
    yield put(actionCreators.guestInhouseProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.guestInhouseProcessReset());
    yield delay(100);
  }
}
function* guestInhouseCheckLockRecord({ payload }: actionTypes.GuestInhouseLockRecord) {
  if (!payload || !payload.register_id) return;
  const url = `${API_URL_V1}pms/registration/guestInHouse/checklockrecord/`;
  const requestBody = {
    register_id: Number(payload.register_id),
    user_name: payload.user_name,
    session_id: String(payload.session_id),
  };
  try {
    const response: ApiResponse = yield call(APIPostRequest, url, requestBody, API_PMS_HEADERS);
    if (response.status === 200) {
      yield put(actionCreators.guestInhouseLockRecord(response.data.result_data));
    } else {
      yield put(actionCreators.guestInhouseProcessReset());
    }
  } catch (error) {
    console.error('❌ Error on check lock record:', error);
  }
}
function* guestInhouseUnLockRecord({ payload }: actionTypes.GuestInhouseUnlockRecord) {
  if (!payload || !payload.register_id) return;
  const { onSuccess, onError } = payload;
  const url = `${API_URL_V1}pms/registration/guestInHouse/unlockrecord/`;
  const requestBody = {
    register_id: Number(payload.register_id),
    user_name: payload.user_name,
    session_id: String(payload.session_id),
  };
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield call(APIPostRequest, url, requestBody, API_PMS_HEADERS);
    if (response.status === 200) {
      const resultData = response.data?.result_data ?? response.data;
      yield put(actionCreators.guestInhouseUnLockRecord(resultData));
      if (typeof onSuccess === 'function') {
        yield call(onSuccess, resultData);
      }
    } else {
      yield put(actionCreators.guestInhouseProcessReset());
      if (typeof onError === 'function') {
        yield call(onError, response);
      }
    }
  } catch (error) {
    console.error('❌ Error on check lock record:', error);
    if (typeof onError === 'function') {
      yield call(onError, error);
    }
  }
}

function* guesetInhouseRequest() {
  yield takeEvery(actionTypes.GUEST_INHOUSE_REQUEST, guestInhouseRequestProcess);
}
function* guestInhouseCheckOut() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_CHECK_OUT, guestInhouseCheckOutProcess);
}

function* guestInhouseCancelCheckIn() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_CANCEL_CHECK_IN, guestInhouseCancelCheckInProcess);
}
function* guestInhouseCancelCheckOut() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_CANCEL_CHECK_OUT, guestInhouseCancelCheckOutProcess);
}

function* guestInhouseDetailRequest() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_DETAIL_REQUEST, guestInhouseDetailRequestProcess);
}

function* guestInhouseVerifyRate() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_VERIFY_RATE, guestInhouseVerifyRateProcess);
}
function* guestInhouseNew() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_NEW, guestInhouseNewProcess);
}
function* guestInhouseQuickWalkIn() {
  yield takeLatest(actionTypes.GUEST_INHOUSE_QUICK_WALKIN, guestInhouseQuickWalkInProcess);
}
function* guestInhouseUpdate() {
  yield takeEvery(actionTypes.GUEST_INHOUSE_UPDATE, guestInhouseUpdateProcess);
}
function* guestInhouseBookingSummary() {
  yield takeEvery(
    actionTypes.GUEST_INHOUSE_BOOKING_SUMMARY_REQUEST,
    guestInhouseBookingSummaryProcess
  );
}
function* guestInhouseCheckinVerify() {
  yield takeEvery(actionTypes.GUEST_INHOUSE_CHECKIN_VERIFY, guestInhouseCheckinVerifyProcess);
}
function* guestInhouseCheckin() {
  yield takeEvery(actionTypes.GUSET_INHOUSE_CHECKIN, guestInhouseCheckinProcess);
}
function* guestInhouseCheckLock() {
  yield takeEvery(actionTypes.GUEST_INHOUSE_LOCK_RECORD, guestInhouseCheckLockRecord);
}
function* guestInhouseUnLock() {
  yield takeEvery(actionTypes.GUEST_INHOUSE_UNLOCK_RECORD, guestInhouseUnLockRecord);
}
export default function* rootSaga() {
  yield all([
    fork(guesetInhouseRequest),
    fork(guestInhouseDetailRequest),
    fork(guestInhouseCheckOut),
    fork(guestInhouseCancelCheckOut),
    fork(guestInhouseCancelCheckIn),
    fork(guestInhouseVerifyRate),
    fork(guestInhouseNew),
    fork(guestInhouseQuickWalkIn),
    fork(guestInhouseUpdate),
    fork(guestInhouseBookingSummary),
    fork(guestInhouseCheckinVerify),
    fork(guestInhouseCheckin),
    fork(guestInhouseCheckLock),
    fork(guestInhouseUnLock),
  ]);
}
